import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputField, Modal } from '../UI';
import { Button } from '../UI/base/Button';
import modalStyles from '../UI/Modal.module.css';
import type { LocationResponse, UserDetailResponse } from '../../api/generated';
import { EMAIL_PATTERN, PERMISSION_FLAGS } from './usersConstants';
import type { PermissionFlag } from './usersConstants';
import styles from './UserFormModal.module.css';

export interface LocationPermissionValue {
  locationId: string;
  read: boolean;
  write: boolean;
  patientBooker: boolean;
}

export interface UserFormValues {
  email: string;
  firstName: string;
  lastName: string;
  accountAdmin: boolean;
  locationPermissions: LocationPermissionValue[];
}

interface UserFormModalProps {
  user: UserDetailResponse | null;
  locations: LocationResponse[];
  onSave: (values: UserFormValues) => void | Promise<void>;
  onClose: () => void;
}

const buildInitialValues = (
  user: UserDetailResponse | null,
  locations: LocationResponse[]
): UserFormValues => ({
  email: user?.email ?? '',
  firstName: user?.firstName ?? '',
  lastName: user?.lastName ?? '',
  accountAdmin: user?.accountAdmin ?? false,
  locationPermissions: locations.map((location) => {
    const existing = user?.locationPermissions.find((p) => p.locationId === location.id);
    return {
      locationId: location.id,
      read: existing?.read ?? false,
      write: existing?.write ?? false,
      patientBooker: existing?.patientBooker ?? false
    };
  })
});

const flagLabelKey: Record<PermissionFlag, string> = {
  read: 'config-user-perm-read',
  write: 'config-user-perm-write',
  patientBooker: 'config-user-perm-booker'
};

export const UserFormModal = ({ user, locations, onSave, onClose }: UserFormModalProps) => {
  const { t } = useTranslation();
  const isEditing = user !== null;
  const [form, setForm] = useState<UserFormValues>(() => buildInitialValues(user, locations));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.firstName.trim()) next.firstName = t('config-user-error-first-name');
    if (!form.lastName.trim()) next.lastName = t('config-user-error-last-name');
    if (!form.email.trim()) {
      next.email = t('config-user-error-email-required');
    } else if (!EMAIL_PATTERN.test(form.email.trim())) {
      next.email = t('config-user-error-email-invalid');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Return the onSave promise so the Save button auto-disables until it settles.
  const handleSave = (): void | Promise<void> => {
    if (!validate()) return;
    return onSave(form);
  };

  const togglePermission = (locationId: string, flag: PermissionFlag) => {
    setForm((prev) => ({
      ...prev,
      locationPermissions: prev.locationPermissions.map((p) =>
        p.locationId === locationId ? { ...p, [flag]: !p[flag] } : p
      )
    }));
  };

  return (
    <Modal
      title={isEditing ? t('config-user-edit') : t('config-user-create')}
      onClose={onClose}
      width={640}
      footer={
        <div className={modalStyles.footerRow}>
          <Button type="secondary" onClick={onClose}>
            {t('save-modal-cancel')}
          </Button>
          <Button type="primary" onClick={handleSave}>
            {t('config-iv-save')}
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        <div className={styles.nameRow}>
          <InputField
            label={t('config-user-first-name')}
            required
            type="text"
            value={form.firstName}
            onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
            error={errors.firstName}
          />
          <InputField
            label={t('config-user-last-name')}
            required
            type="text"
            value={form.lastName}
            onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
            error={errors.lastName}
          />
        </div>

        <InputField
          label={t('config-user-email')}
          required
          type="text"
          value={form.email}
          disabled={isEditing}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          error={errors.email}
        />

        <label className={styles.toggleRow}>
          <input
            type="checkbox"
            checked={form.accountAdmin}
            onChange={(e) => setForm((f) => ({ ...f, accountAdmin: e.target.checked }))}
          />
          <span>{t('config-user-account-admin')}</span>
        </label>

        <div className={styles.permissionsSection}>
          <span className={styles.sectionLabel}>{t('config-user-location-permissions')}</span>
          {locations.length === 0 ? (
            <p className={styles.adminNote}>{t('config-user-no-locations')}</p>
          ) : (
            <div className={styles.grid}>
              <div className={`${styles.gridRow} ${styles.gridHeader}`}>
                <span className={styles.gridLocation}>{t('config-user-col-location')}</span>
                {PERMISSION_FLAGS.map((flag) => (
                  <span key={flag} className={styles.gridFlag}>
                    {t(flagLabelKey[flag])}
                  </span>
                ))}
              </div>
              {form.locationPermissions.map((perm) => {
                const location = locations.find((l) => l.id === perm.locationId);
                return (
                  <div key={perm.locationId} className={styles.gridRow}>
                    <span className={styles.gridLocation}>{location?.name ?? perm.locationId}</span>
                    {PERMISSION_FLAGS.map((flag) => (
                      <span key={flag} className={styles.gridFlag}>
                        <input
                          type="checkbox"
                          checked={perm[flag]}
                          onChange={() => togglePermission(perm.locationId, flag)}
                        />
                      </span>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
